import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { metadataStore } from '../metadataStore';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_DMS_READ, ADMIN_DMS_WRITE } from 'constants/rightsCodes';
import { SAVE_METADATA_LIST } from '../mutations';
import { METADATA_LIST_SAVED } from '../subscriptions';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { Prompt } from 'react-router-dom';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { Portal, TableCell, TableRow } from '@mui/material';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import MetadataDetailItemComponent from './MetadataDetailItemComponent';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import useSubscription from 'hooks/apollo/useSubscription';
import { useMutation } from 'hooks/apollo/useMutation';
import { stopOnFirstFailure, items, required, shape } from '@totalsoft/pure-validations';
import { uniqueField } from 'utils/simple-validations';
import { getErrors, useValidation } from '@totalsoft/pure-validations-react';
import { StyledTableRow } from 'components/common/Styles';
import { ThemedWrapper } from 'components/common/SurfaceStyles';

const validator = items(
  shape({
    code: [required, uniqueField] |> stopOnFirstFailure,
    name: [required, uniqueField] |> stopOnFirstFailure,
    dataType: required
  })
);

const getEmptyMetadataItem = () => ({
  id: crypto.randomUUID(),
  code: '',
  name: '',
  dataType: '',
  entityType: '',
  isNew: true,
  originalIndex: 0
});

const MetadataDetailListComponent = ({ columnsLength }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const { store } = useListBrowserUtils(metadataStore);
  const canRead = usePermission(ADMIN_DMS_READ);
  const canWrite = usePermission(ADMIN_DMS_WRITE);

  const [pending, setPending] = useState(false);

  const [lens, dirtyInfo] = useChangeTrackingLens(store.allValues.map((value, index) => ({ ...value, originalIndex: index })));
  const [validation, validate] = useValidation(validator);

  const getDetails = () => {
    let newDetails = [],
      existingDetails = [];

    sequence(lens).forEach(detailLens => {
      if (detailLens.isNew |> get) newDetails.push(detailLens);
      if (store.values.some(item => item.id === (detailLens.id |> get))) existingDetails.push(detailLens);
    });
    // "Sort" the existing details to reflect the order in the store values
    existingDetails = store.values
      .map(item => existingDetails.find(detailLens => (detailLens.id |> get) === item.id))
      .filter(item => Boolean(item));
    return [...newDetails, ...existingDetails];
  };

  const handleAdd = () => {
    const current = lens |> get;
    (lens |> set)([getEmptyMetadataItem(), ...current.map(value => ({ ...value, originalIndex: value.originalIndex + 1 }))]);
  };

  const handleDelete = id => {
    const current = lens |> get;
    (lens |> set)(current.filter(metadata => metadata.id !== id));
  };

  // #region Save

  const [saveMetadataList] = useMutation(SAVE_METADATA_LIST, {
    onError: _ => {
      store.refetch();
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;
    const inputItems = data.map(item => {
      const inputItem = { ...item };
      if (inputItem.isNew) {
        inputItem.id = null;
        delete inputItem.isNew;
      }
      delete inputItem.originalIndex;
      return inputItem;
    });

    setPending(true);

    saveMetadataList({ variables: { input: inputItems } });
  };

  useSubscription(METADATA_LIST_SAVED, {
    onData: ({ client }) => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'metadata' });
      client.cache.gc();
      addToast(t('Metadata.MetadataSaved'), 'success');
      setTimeout(() => {
        setPending(false);
      }, 100);
    }
  });

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const details = getDetails();

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      {details.map(detailLens => (
        <MetadataDetailItemComponent
          lens={detailLens}
          onDelete={handleDelete}
          validation={validation[detailLens.originalIndex |> get]}
          dirtyInfo={dirtyInfo[detailLens.originalIndex |> get]}
          pending={pending}
          key={detailLens.id |> get}
        />
      ))}
      {details.length === 0 && (
        <TableRow>
          <TableCell colSpan={columnsLength}>
            <Typography variant='subtitle2' fontStyle='italic' color='textPrimary' textAlign='center'>
              {t('ListBrowser.NoEntries')}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {getErrors(validation) && (
        <StyledTableRow>
          <TableCell colSpan={columnsLength}>
            <ThemedWrapper color='error' flexDirection='column'>
              {getErrors(validation)
                .split(',')
                .map(error => (
                  <span key={error}>{error}</span>
                ))}
            </ThemedWrapper>
          </TableCell>
        </StyledTableRow>
      )}
      <Portal container={() => document.getElementById('metadata-actions-slot')}>
        <IconButton
          color='transparent'
          tooltip={t('Tooltips.Metadata.AddNewItem')}
          onClick={handleAdd}
          disabled={!canRead || !canWrite || pending}
        >
          <AddIcon />
        </IconButton>
        <IconButton color='transparent' tooltip={t('Save')} onClick={handleSave} disabled={!isDirty || !canRead || !canWrite || pending}>
          <SaveIcon />
        </IconButton>
      </Portal>
    </>
  );
};

MetadataDetailListComponent.propTypes = {
  columnsLength: PropTypes.number
};

export default MetadataDetailListComponent;
